import { type GeoLocation } from "@src/appV2/Location";
import {
  type ClaimShiftRequest,
  useClaimShift,
} from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useCallback, useState } from "react";

import { type OpenBundleShift } from "../types";

interface FailedShift {
  shiftId: string;
  error: unknown;
}

interface BookBundleShiftsResult {
  bookedShiftIds: string[];
  failedShifts: FailedShift[];
}

export function useBookBundleShifts(bundleShifts: OpenBundleShift[]) {
  const { mutateAsync: claimShift } = useClaimShift();
  const searchMode = useShiftDiscoverySearchMode();
  const [isBooking, setIsBooking] = useState(false);

  const bookBundleShifts = useCallback(
    async (
      selectedShiftIds: Set<string>,
      geoLocation?: GeoLocation
    ): Promise<BookBundleShiftsResult> => {
      setIsBooking(true);

      try {
        const shiftsToBook = bundleShifts.filter((shift) => selectedShiftIds.has(shift.id));

        const claimRequests: ClaimShiftRequest[] = shiftsToBook.map((shift) => ({
          shiftId: shift.id,
          offerId: shift.relationships.offer.data.id,
          searchMode,
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude,
        }));

        const results = await Promise.allSettled(
          claimRequests.map(async (request) => {
            await claimShift(request);
            return request.shiftId;
          })
        );

        const bookedShiftIds: string[] = [];
        const failedShifts: FailedShift[] = [];

        for (const [index, result] of results.entries()) {
          if (result.status === "fulfilled") {
            bookedShiftIds.push(result.value);
          } else {
            failedShifts.push({
              shiftId: claimRequests[index].shiftId,
              error: result.reason,
            });
          }
        }

        return { bookedShiftIds, failedShifts };
      } finally {
        setIsBooking(false);
      }
    },
    [bundleShifts, claimShift, searchMode]
  );

  return {
    bookBundleShifts,
    isBooking,
  };
}
